import get from 'lodash/get';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';

import { DialogTitleWithClose, RangeSlider, SelectWithSearch, GroupedSelect } from 'ui';
import { creditRatingRangeValues } from 'shared/constants/creditRatingRangeValues';
import { useGroupedAutocompleteStyles } from 'shared/styles/groupedAutocompleteStyles/groupedAutocompleteStyles';
import { useRegions } from 'shared/hooks';
import { getSortedFlattenedIndustries } from 'selectors/common';
import { useDeepCompareEffectNoCheck as useDeepCompareEffect } from 'use-deep-compare-effect';

const CreditRatingEntitiesFiltersModal = ({
  open,
  onClose,
  onSubmit,
  searchQuery,
  initialFilter,
  filterableIndustries,
  allowEmptyIndustriesFilter,
}) => {
  const groupedAutocompleteClasses = useGroupedAutocompleteStyles();
  const { t } = useTranslation();
  const industriesData = useSelector(getSortedFlattenedIndustries);
  const regions = useRegions(searchQuery);

  const ratingTypes = [
    { value: 'PUBLIC', label: t('Public') },
    { value: 'ON_REQUEST', label: t('Subscription') },
  ];

  const actionTypes = [
    { value: 'upgrade', label: t('Upgrade') },
    { value: 'downgrade', label: t('Downgrade') },
    { value: 'new', label: t('New') },
    { value: 'review', label: t('Review') },
    { value: 'default', label: t('Default') },
    { value: 'conversion to solicited', label: t('Conversion to solicited') },
    { value: 'outlook/trend', label: t('Outlook/Trend') },
    { value: 'withdrawal', label: t('Withdrawal') },
    { value: 'affirmed', label: t('Affirmed') },
  ];

  const getInitialValues = () => ({
    countries: [],
    industries: [],
    ratingType: null,
    ratingRange: [
      creditRatingRangeValues[0].value,
      creditRatingRangeValues[creditRatingRangeValues.length - 1].value,
    ],
    actionType: null,
    ...initialFilter,
  });

  const [values, setValues] = useState(getInitialValues());

  const [industries, setIndustries] = useState([]);

  useDeepCompareEffect(() => {
    setValues(getInitialValues);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  useEffect(() => {
    setIndustries(filterableIndustries || industriesData);
  }, [industriesData, filterableIndustries]);

  const handleChange = name => value => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleIndustriesChange = (_event, sectors) => {
    let newSectors = sectors;
    if (!allowEmptyIndustriesFilter && !sectors.length) {
      const initialIndustry = get(initialFilter, 'industries[0]');
      newSectors = initialIndustry ? [initialIndustry] : [];
    }
    setValues({
      ...values,
      industries: newSectors,
    });
  };

  const handleSubmit = () => {
    const { ratingRange } = values;

    const cleanedValues = {
      ...values,
      countries: values.countries.map(({ iso }) => iso),
      industrySectors: values.industries.map(({ code, industryCode }) => ({
        sector: code,
        industry: industryCode,
      })),
      longTermRatingFrom: encodeURIComponent(creditRatingRangeValues[ratingRange[0] - 1].label),
      longTermRatingTo: encodeURIComponent(creditRatingRangeValues[ratingRange[1] - 1].label),
    };

    onSubmit(cleanedValues);
  };

  const handleRatingRangeChange = (_event, newValues) => {
    setValues({
      ...values,
      ratingRange: newValues,
    });
  };

  const isValueRemovable = () => {
    return allowEmptyIndustriesFilter || values.industries.length > 1;
  };

  return (
    <Dialog fullWidth open={open}>
      <DialogTitleWithClose onClose={onClose}>{t('Apply filters')}</DialogTitleWithClose>
      <DialogContent>
        <GroupedSelect
          multiple
          options={regions.data}
          value={values.countries}
          label={t('Country')}
          onChange={handleChange('countries')}
          getOptionLabel={option => option.name}
          groupBy={option => option.continent}
        />
        <Autocomplete
          multiple
          options={industries}
          onChange={handleIndustriesChange}
          getOptionLabel={option => option.name}
          groupBy={option => option.industryName}
          value={values.industries}
          renderInput={params => (
            <TextField {...params} label={t('Industries')} variant="outlined" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.name}
                {...getTagProps({ index })}
                disabled={!isValueRemovable()}
              />
            ))
          }
          classes={groupedAutocompleteClasses}
        />
        <SelectWithSearch
          label={t('Rating type')}
          options={ratingTypes}
          value={values.ratingType}
          onChange={handleChange('ratingType')}
        />
        <Box mt={2} mb={1}>
          <RangeSlider
            label={t('Entity credit rating range')}
            options={creditRatingRangeValues}
            values={values.ratingRange}
            onChange={handleRatingRangeChange}
          />
        </Box>
        <SelectWithSearch
          label={t('Action type')}
          options={actionTypes}
          value={values.actionType}
          onChange={handleChange('actionType')}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {t('Apply filters')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreditRatingEntitiesFiltersModal.defaultProps = {
  searchQuery: null,
  initialFilter: {},
  filterableIndustries: null,
  allowEmptyIndustriesFilter: true,
};

CreditRatingEntitiesFiltersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  initialFilter: PropTypes.object,
  filterableIndustries: PropTypes.arrayOf(PropTypes.object),
  allowEmptyIndustriesFilter: PropTypes.bool,
};

export default CreditRatingEntitiesFiltersModal;
